import React, { useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import ls from './Loads.module.css'
import lc from './List.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { SdPanel } from './SdPanel'
import { ExportIcon, HeaderAction } from './chrome'
import { headerStyles } from '../../components/PageHeader'
import { workbook, XLSX_TYPE } from '../batches/xlsx'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { apiJson } from '../../lib/api'
import { useLayer, useChanged } from '../../components/layers'
import { useHelpPref } from '../../lib/preferences'
import { hasCapability, useAccess } from '../../lib/access'
import { SkeletonRows } from '../../components/pending'
import { todayIso, type Chip } from './orders'
import { DetailsFields, LoadModal, type LoadTab } from './LoadModal'
import { dropUrlParams, oneOf, urlParam } from './stock'
import {
  LOAD_FILTERS,
  exportRows,
  filterLoads,
  formValid,
  loadChip,
  newForm,
  ordersText,
  palletsText,
  vehicleText,
  whenText,
  type DetailsForm,
  type Load,
  type LoadFilter,
  type LoadOptions,
  type LoadRecord,
} from './loads'
import { openableRow } from '../../lib/openableRow'

// The Dispatches list (V4 Stock → Dispatches; handoff §7n.6): one row per
// load, i.e. per vehicle leaving. Every figure and state is the API's.

const ChipEl = ({ c }: { c: Chip }) => (
  <span className={styles.chip} style={{ background: c.bg, color: c.ink }}>
    {c.label}
  </span>
)

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function DispatchesView({ user, loads, onChanged, showingLoading }: { user: any; loads: Load[]; onChanged: () => void; showingLoading?: boolean }) {
  const [q, setQ] = useState('')
  const [state, setState] = useState<LoadFilter>(() => oneOf(urlParam('state'), LOAD_FILTERS.map(([k]) => k), ''))
  const [open, setOpen] = useState<{ id: string; tab: LoadTab } | null>(() => {
    const id = urlParam('open')
    return id ? { id, tab: 'load' } : null
  })
  const [creating, setCreating] = useState(false)
  const mayManage = hasCapability(useAccess(), 'manage_stock_dispatch')
  const [toast, setToast] = useState('')

  // A deep link from the Overview (handoff §7n.1a) sets where this starts.
  useEffect(() => dropUrlParams(['state', 'open']), [])
  const [helpOn, toggleHelp] = useHelpPref(user, 'loads')
  const { startTask } = useTasks()

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const shown = filterLoads(loads, state, q)

  const doExport = () => {
    const list = shown
    startTask(fileTask('Export loads', list.length + ' load' + (list.length === 1 ? '' : 's') + ' as shown', 'loads.xlsx', XLSX_TYPE, () => workbook([{ name: 'Loads', rows: exportRows(list) }])))
  }

  return (
    <div className={styles.wrap}>
      <div className={lc.toolbar}>
        <input className={`${lc.search} ${lc.searchNarrow}`} placeholder="Load, carrier, booking…" aria-label="Search loads" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={lc.select} aria-label="Status" value={state} onChange={(e) => setState(e.target.value as LoadFilter)}>
          {LOAD_FILTERS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <button type="button" className={lc.helpPill} onClick={toggleHelp} aria-expanded={helpOn}>
          {helpOn ? 'Hide help' : 'How loads work'}
        </button>
        <span className={lc.stats} data-testid="loads-count">
          {shown.length} of {loads.length} load{loads.length === 1 ? '' : 's'}
        </span>
        <button type="button" className={lc.exportBtn} onClick={doExport}>
          <ExportIcon />
          Export current results
        </button>
        {mayManage && (
          <HeaderAction>
            <button type="button" className={headerStyles.btnPrimary} onClick={() => setCreating(true)}>
              New load
            </button>
          </HeaderAction>
        )}
      </div>

      {helpOn && <LoadsHelp onClose={toggleHelp} />}

      <div className={lc.card}>
        <div className={`${ls.listGrid} ${styles.head}`}>
          <span>Load</span>
          <span>Planned departure</span>
          <span>Carrier</span>
          <span>Vehicle · booking</span>
          <span>Orders</span>
          <span>Pallets</span>
          <span>Boxes</span>
          <span>Status</span>
        </div>
        {showingLoading ? (
          <SkeletonRows rowClass={`${ls.listGrid} ${styles.line} ${lc.roomy}`} cols={8} label="Loading dispatches" />
        ) : shown.length === 0 ? (
          <div className={lc.empty}>{loads.length ? 'No loads match.' : 'No loads yet. Create one with New load.'}</div>
        ) : (
          shown.map((l) => (
            <div key={l.id} className={`${ls.listGrid} ${styles.line} ${lc.roomy} ${styles.clickable}`} {...openableRow(() => setOpen({ id: l.id, tab: 'load' }))} data-testid="load-row">
              <span className={styles.strong}>{l.reference}</span>
              <span className={l.planned_date ? undefined : styles.muted}>{whenText(l)}</span>
              <span className={styles.ellipsis}>{l.carrier?.name || '—'}</span>
              <span className={`${styles.muted} ${styles.ellipsis}`}>{vehicleText(l)}</span>
              <span>{ordersText(l.orders)}</span>
              <span className={styles.stack}>
                <span className={styles.num}>{palletsText(l)}</span>
                {l.discrepancy && <span className={ls.discFlag}>Discrepancy</span>}
              </span>
              <span className={styles.num}>{l.boxes} boxes</span>
              <span>
                <ChipEl c={loadChip(l.state)} />
              </span>
            </div>
          ))
        )}
      </div>

      {creating && (
        <NewLoadPanel
          user={user}
          loads={loads}
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false)
            setToast(r.message)
            onChanged()
            setOpen({ id: r.record.load.id, tab: 'op' })
          }}
        />
      )}
      {open && (
        <LoadModal
          key={open.id}
          user={user}
          loadId={open.id}
          initialTab={open.tab}
          onClose={() => setOpen(null)}
          onChanged={(msg) => {
            if (msg) setToast(msg)
            onChanged()
          }}
        />
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function NewLoadPanel({
  user,
  loads,
  onClose,
  onCreated,
}: {
  user: any
  loads: Load[]
  onClose: () => void
  onCreated: (r: { message: string; record: LoadRecord }) => void
}) {
  const [options, setOptions] = useState<LoadOptions | null>(null)
  const [form, setForm] = useState<DetailsForm | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    apiJson<LoadOptions>(user, '/api/dispatches/options')
      .then((o) => {
        if (!live) return
        setOptions(o)
        setForm(newForm(loads, o, todayIso()))
      })
      .catch((e) => live && setErrors([`Failed to load carriers and locations: ${errText(e)}`]))
    return () => {
      live = false
    }
    // The defaults are taken once, when the panel opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // A guarded layer (h 425-458): the defaults from newForm are the start, so
  // a panel nobody touched closes silently. The first field takes focus once
  // the options have loaded.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(form, form !== null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: form !== null })

  const create = async () => {
    if (!form) return
    setBusy(true)
    try {
      onCreated(await apiJson<{ message: string; record: LoadRecord }>(user, '/api/dispatches', { method: 'POST', body: form }))
    } catch (e: any) {
      setErrors(Array.isArray(e?.body?.errors) && e.body.errors.length ? e.body.errors : [errText(e)])
    } finally {
      setBusy(false)
    }
  }

  return (
    <SdPanel
      title="New load"
      onCancel={requestClose}
      confirm={confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <button type="button" className={o.primary} disabled={busy || !form || !formValid(form)} onClick={create}>
          Create load
        </button>
      }
    >
          <p className={sd.text}>
            A load is one vehicle leaving. Book the truck here, then add the pallets it will carry. The load number is given when you create it.
          </p>
          {options && form && options.carriers.length === 0 && (
            <div className={styles.alert} role="alert">
              No carriers are offered yet, so a load cannot be created. A load needs a carrier: add or restore one under Configuration → Stock &amp; Dispatch → Carriers &amp; forwarders.
            </div>
          )}
          {options && form ? <DetailsFields form={form} set={(p) => setForm((f) => (f ? { ...f, ...p } : f))} options={options} /> : !errors.length && <div className={styles.muted}>Loading…</div>}
          {errors.length > 0 && (
            <div className={styles.errList} role="alert">
              {errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </div>
          )}
    </SdPanel>
  )
}

function LoadsHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className={lc.help} data-testid="loads-help">
      <div className={lc.helpHead}>
        <span className={lc.helpTitle}>How loads work</span>
        <span className={lc.helpLead}>Newest departure day first; within a day, earliest time first.</span>
        <button type="button" className={lc.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={lc.helpItem}>
        <span>One row is one vehicle leaving</span>
        <p>
          A load carries pallets, and those pallets can belong to several orders; the Orders column counts them. Carrier, vehicle and booking are load
          data. Airway bill, flight, container and receiving establishment stay on each order, so a shared truck never mixes one customer’s paperwork
          with another’s.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Draft → Ready to load → Loading → Dispatched</span>
        <p>
          A load is ready only when every pallet on it passes every gate: confirmed, no discrepancy, no hold on the pallet or its source, order approved,
          applied lid matching the required lid, and not on another active load. A load that is not ready names the pallet and the gate.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Verification is a set, not a count</span>
        <p>
          The verified pallets must be exactly the planned pallets. The right number with a wrong identity is blocked and the identity named, and a
          scan of a pallet that is not planned is blocked too. <strong>With discrepancies</strong> in the status filter finds loads in that state.
          Verify all by sight is allowed, and is recorded as a manual action against your name.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>What departure changes</span>
        <p>
          Boxes leave stock and orders’ dispatched figures move only when departure is confirmed. Documents are Draft until then, and the final
          versions reflect what actually departed. Removing a pallet before departure clears its verification and returns it to stock with the order
          quantity unchanged.
        </p>
      </div>
    </div>
  )
}
